import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";

interface Props {
  sessionId: string;
  /** Called once the session is gone, for pages that cannot stay where they are. */
  onDeleted?: () => void;
}

/**
 * Deleting takes the lap summaries and the Parquet trace with it and cannot be
 * undone, so it asks first — inline rather than in a dialog, since the row or header
 * it sits in is the context the question needs.
 */
export function DeleteSessionButton({ sessionId, onDeleted }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.deleteSession(sessionId),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ["session", sessionId] });
      await queryClient.invalidateQueries({ queryKey: ["sessions"] });
      onDeleted?.();
    },
  });

  if (remove.isError) {
    return (
      <span className="text-xs text-red-400" title={remove.error.message}>
        {t("session.delete.failed")}
      </span>
    );
  }

  if (remove.isPending) {
    return <span className="text-xs text-zinc-500">{t("session.delete.pending")}</span>;
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-400 transition-colors hover:border-red-800 hover:text-red-400"
      >
        {t("session.delete.action")}
      </button>
    );
  }

  return (
    <span className="flex items-center justify-end gap-2 text-xs">
      <button
        type="button"
        onClick={() => remove.mutate()}
        className="rounded-md bg-red-900/60 px-2 py-1 font-medium text-red-100 transition-colors hover:bg-red-800"
      >
        {t("session.delete.confirm")}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="text-zinc-500 transition-colors hover:text-zinc-300"
      >
        {t("session.delete.cancel")}
      </button>
    </span>
  );
}
